import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { map, isEmpty } from 'lodash';
import { Link } from 'react-router-dom';
import MediaQuery from 'react-responsive';

import { PATH } from '../../../../constants/path';
import { CONTEXT_ROOT, STATIC_ROOT } from '../../../../constants';
import { MOMENTJS_DATE_FORMAT, MOMENTJS_DATE_FORMAT_STANDARD } from '../../../../constants/moment';

import { fetchContactRequestReasonIfNeeded } from '../../../../services/contactrequest/actions';
import { fetchAccountMessagesIfNeeded } from '../../../../services/account/mymessages/actions';

import { toDate } from '../../../../utils/dateUtils';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import SvgComponent from '../../../SvgComponent';
import HeadComponent from '../../../HeadComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';

import './MyMessagesPage.scss';

const DISPLAY_STEP = 5;

const ICON_VALIDATED = 'validation';
const ICON_WAITING = 'pending';

const renderIcon = (icon, width, height) => (
    <SvgComponent
        src={`${STATIC_ROOT}/icon/picto-${icon}.svg`}
        width={width}
        height={height}
        aria-hidden="true"
        focusable="false"
    />
);

const renderMessageState = (icon, text, width, height) => (
    <div className="message-caption-block center-mobile">
        {renderIcon(icon, width, height)}
        <p className="hide-mobile">{text}</p>
    </div>
);

export class MyMessagesPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { index: DISPLAY_STEP };
    }

    componentDidMount() {
        this.props.fetchContactRequestReasonIfNeeded();
        this.props.fetchAccountMessagesIfNeeded();
    }

    renderConditionnalOnMessageState(state) {
        if (!state) return null;

        const { i18n } = this.props;
        if (state.id === 3) {
            return renderMessageState(ICON_VALIDATED, i18n.MESSAGES_CAPTION.DONE, 24, 24);
        } else {
            return renderMessageState(ICON_WAITING, i18n.MESSAGES_CAPTION.WAITING, 24, 24);
        }
    }

    renderConditionnalOnAnswerDate(answerDate) {
        if (!answerDate) return null;
        if (answerDate > 0) {
            return toDate(answerDate, MOMENTJS_DATE_FORMAT);
        }
    }

    renderContactRequestReasonLabel(id) {
        const { UNDEFINED } = this.props.common;
        const reason = this.props.contactrequestreasons.find(e => e.id === id);
        if (isEmpty(reason)) return UNDEFINED;
        return reason.label;
    }

    renderSeeMoreButton() {
        const { i18n, data } = this.props;
        const { index } = this.state;

        if (data.length >= DISPLAY_STEP && index < data.length) {
            return (
                <div className="messages-button">
                    <span
                        id="more-messages-button"
                        title={i18n.SEE_MORE_MESSAGES}
                        aria-label={i18n.SEE_MORE_MESSAGES}
                        role="button"
                        tabIndex="0"
                        onClick={this.handleSeeMoreClick.bind(this)}
                        onKeyDown={this.handleKeyDown}>
                        <span className="plus">+</span>
                        <span className="text">{i18n.SEE_MORE_MESSAGES}</span>
                    </span>
                </div>
            );
        }
    }

    handleSeeMoreClick() {
        this.setState({
            index: this.state.index + DISPLAY_STEP
        });
    }

    handleKeyDown = e => {
        if (e.key === 'Enter') {
            this.handleSeeMoreClick();
        }
    };

    render() {
        const { i18n, data, fetching } = this.props;
        const { index } = this.state;

        return (
            <div id="p-account-messages" className="container">
                <HeadComponent
                    title={i18n.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${PATH.ACCOUNT_MESSAGES_PATH}`}
                />
                <h3>{i18n.TITLE}</h3>

                <p id="table-caption">{i18n.TABLE_CAPTION}</p>
                <table aria-describedby="table-caption">
                    <thead>
                        <MediaQuery query="(min-width: 769px)">
                            {matches =>
                                matches ? (
                                    <tr>
                                        <th role="columnheader">{i18n.MESSAGE_DATE_DESKTOP}</th>
                                        <th role="columnheader" className="reason">
                                            {i18n.REQUEST_REASON_DESKTOP}
                                        </th>
                                        <th role="columnheader" className="state">
                                            {i18n.REQUEST_STATE_DESKTOP}
                                        </th>
                                        <th role="columnheader">{i18n.DETAILS}</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th role="columnheader">{i18n.MESSAGE_DATE_MOBILE}</th>
                                        <th role="columnheader" className="reason">
                                            {i18n.REQUEST_REASON_MOBILE}
                                        </th>
                                        <th role="columnheader" className="state">
                                            {i18n.REQUEST_STATE_MOBILE}
                                        </th>
                                        <th role="columnheader" className="details">
                                            {i18n.DETAILS}
                                        </th>
                                    </tr>
                                )
                            }
                        </MediaQuery>
                    </thead>
                    <tbody>
                        {fetching ? (
                            <tr>
                                <MediaQuery query="(min-width: 769px)">
                                    {matches => (
                                        <td colSpan={matches ? '4' : '4'}>
                                            <LoadingComponent />
                                        </td>
                                    )}
                                </MediaQuery>
                            </tr>
                        ) : data && data.length > 0 ? (
                            map(data.slice(0, index), message => (
                                <tr key={message.id}>
                                    <MediaQuery query="(min-width: 769px)">
                                        {matches => (
                                            <td>
                                                {matches
                                                    ? toDate(
                                                          message.requestDate,
                                                          MOMENTJS_DATE_FORMAT
                                                      )
                                                    : toDate(
                                                          message.requestDate,
                                                          MOMENTJS_DATE_FORMAT_STANDARD
                                                      )}
                                            </td>
                                        )}
                                    </MediaQuery>
                                    <td className="reason">
                                        {this.renderContactRequestReasonLabel(
                                            message.contactRequestReasonId
                                        )}
                                    </td>
                                    <td className="picto">
                                        {this.renderConditionnalOnMessageState(message.state)}
                                    </td>
                                    <td className="details">
                                        <MediaQuery query="(min-width: 769px)">
                                            {matches => (
                                                <Link
                                                    to={`${PATH.ACCOUNT_MESSAGES_PATH}/${message.id}`}
                                                    title={convertHtmlToReactDomWithParams(
                                                        i18n.SHOW_MESSAGE_DETAILS_TITLE,
                                                        toDate(
                                                            message.requestDate,
                                                            MOMENTJS_DATE_FORMAT
                                                        ),
                                                        this.renderContactRequestReasonLabel(
                                                            message.contactRequestReasonId
                                                        )
                                                    )}>
                                                    {matches
                                                        ? i18n.SHOW_MESSAGE_DETAILS_DESKTOP
                                                        : i18n.SHOW_MESSAGE_DETAILS_MOBILE}
                                                </Link>
                                            )}
                                        </MediaQuery>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <MediaQuery query="(min-width: 769px)">
                                    {matches => (
                                        <td className="empty" colSpan={matches ? '4' : '4'}>
                                            {i18n.NO_MESSAGES}
                                        </td>
                                    )}
                                </MediaQuery>
                            </tr>
                        )}
                    </tbody>
                </table>

                {this.renderSeeMoreButton()}

                <section className="message-caption">
                    <div className="message-caption-block">
                        {renderIcon(ICON_VALIDATED, 24, 24)}
                        <p>{i18n.MESSAGES_CAPTION.DONE}</p>
                    </div>
                    <div className="message-caption-block">
                        {renderIcon(ICON_WAITING, 24, 24)}
                        <p>{i18n.MESSAGES_CAPTION.WAITING}</p>
                    </div>
                </section>
                <section className="message-bottom">
                    {i18n.FOOTER_TEXT}
                    <Link to={PATH.FAQ_PATH}>FAQ</Link> ?&nbsp;
                    <Link to={PATH.CONTACTREQUEST_PATH}>{i18n.CONTACT_CUSTOMER_SERVICE}</Link>.
                </section>
            </div>
        );
    }
}

MyMessagesPage.propTypes = {
    common: PropTypes.object.isRequired,
    i18n: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    contactrequestreasons: PropTypes.array.isRequired
};

function mapStateToProps(state) {
    return {
        common: state.i18n.common,
        i18n: state.i18n.page.ACCOUNT.MESSAGES,
        data: state.account.mymessages.messages || [],
        fetching: state.account.mymessages.fetching,
        contactrequestreasons: state.contactrequest
            ? state.contactrequest.contactrequestreasons
            : []
    };
}

const mapDispatchToProps = {
    fetchContactRequestReasonIfNeeded,
    fetchAccountMessagesIfNeeded
};

export default connect(mapStateToProps, mapDispatchToProps)(MyMessagesPage);



// WEBPACK FOOTER //
// ./src/components/pages/account/mymessages/MyMessagesPage.js